import axiosNew from '../../helpers/axios'

/**
 * POST support/main-task/epics
 * @param {{
 *   projectId: string,
 *   filter?: object,
 *   sort?: { field: string, order: string }
 * }} payload
 */
export const fetchMainTaskEpics = (payload) =>
    axiosNew({
        url: 'support/main-task/epics',
        method: 'POST',
        data: payload,
    })

/**
 * POST support/main-task/sprints
 * @param {{
 *   projectId: string,
 *   search?: string,
 *   filter?: object,
 *   sort?: { field: string, order: string }
 * }} payload
 */
export const fetchMainTaskSprints = (payload) =>
    axiosNew({
        url: 'support/main-task/sprints',
        method: 'POST',
        data: payload,
    })

/**
 * POST support/main-task/sprint — create a sprint.
 * @param {{
 *   projectId: string,
 *   title: string,
 *   start: string,
 *   end: string
 * }} payload
 */
export const createMainTaskSprint = (payload) =>
    axiosNew({
        url: 'support/main-task/sprint',
        method: 'POST',
        data: payload,
    })

/**
 * PUT support/main-task/sprint/:id — update sprint (start/complete, dates, title).
 * @param {{
 *   sprintId: string,
 *   title: string,
 *   start: string,
 *   end: string,
 *   isActive: boolean,
 *   isComplete: boolean
 * }} args
 */
export const updateMainTaskSprint = ({
    sprintId,
    title,
    start,
    end,
    isActive,
    isComplete,
}) =>
    axiosNew({
        url: `support/main-task/sprint/${encodeURIComponent(sprintId)}`,
        method: 'PUT',
        data: { title, start, end, isActive, isComplete },
    })

/**
 * GET support/main-task/sprint/:id — sprint detail.
 * @param {string} sprintId
 */
export const fetchMainTaskSprintById = (sprintId) =>
    axiosNew({
        url: `support/main-task/sprint/${encodeURIComponent(sprintId)}`,
        method: 'GET',
    })

/**
 * GET support/main-task/sprint/:id/summary — completion summary and destination sprints.
 * @param {string} sprintId
 */
export const fetchMainTaskSprintSummaryById = (sprintId) =>
    axiosNew({
        url: `support/main-task/sprint/${encodeURIComponent(sprintId)}/summary`,
        method: 'GET',
    })

/**
 * POST support/main-task/sprint/:id/complete — complete sprint.
 * When there are open tasks, pass moveOpenTasksTo (+ newSprint when moving to a new sprint).
 * When all tasks are already complete, moveOpenTasksTo can be omitted.
 * @param {{ sprintId: string, moveOpenTasksTo?: string, newSprint?: { title: string, start: string, end: string } }} args
 */
export const completeMainTaskSprint = ({
    sprintId,
    moveOpenTasksTo,
    newSprint,
}) => {
    const data = {}
    if (moveOpenTasksTo) {
        data.moveOpenTasksTo = moveOpenTasksTo
        if (moveOpenTasksTo === 'new' && newSprint) {
            data.newSprint = newSprint
        }
    }
    return axiosNew({
        url: `support/main-task/sprint/${encodeURIComponent(sprintId)}/complete`,
        method: 'PUT',
        data,
    })
}

/**
 * DELETE support/main-task/sprint/:id — delete sprint and move tasks.
 * @param {{ sprintId: string, moveTasksTo: string }} args
 */
export const deleteMainTaskSprint = ({ sprintId, moveTasksTo }) =>
    axiosNew({
        url: `support/main-task/sprint/${encodeURIComponent(sprintId)}`,
        method: 'DELETE',
        data: { moveTasksTo },
    })

/**
 * PUT support/main-task/:id/move — move task between sprint lanes.
 * @param {{
 *  taskId: string,
 *  projectId: string,
 *  sprintId: string | null,
 *  status: string,
 *  initialPosition: number,
 *  position: number,
 *  epic?: string
 * }} payload
 */
export const moveMainTask = ({ taskId, ...payload }) =>
    axiosNew({
        url: `support/main-task/${encodeURIComponent(taskId)}/move`,
        method: 'PUT',
        data: payload,
    })

/**
 * Normalize body from GET sprint/:id (unwraps { data } when present).
 * @param {*} apiBody — axios response.data or unwrapped payload
 */
export const normalizeMainTaskSprintDetailPayload = (apiBody) => {
    if (apiBody == null || typeof apiBody !== 'object') return null
    if (Object.prototype.hasOwnProperty.call(apiBody, 'data')) {
        const inner = apiBody.data
        if (
            inner != null &&
            typeof inner === 'object' &&
            !Array.isArray(inner)
        ) {
            return inner
        }
        return null
    }
    return apiBody
}

/**
 * Normalize body from GET sprint/:id/summary.
 * Expects { data: { completedCount, openCount, otherSprints } } or direct object.
 * @param {*} apiBody
 */
export const normalizeMainTaskSprintSummaryPayload = (apiBody) => {
    if (apiBody == null || typeof apiBody !== 'object') return null
    const root = Object.prototype.hasOwnProperty.call(apiBody, 'data')
        ? apiBody.data
        : apiBody
    if (root == null || typeof root !== 'object') return null
    const completedCount = Number(root.completedCount ?? 0)
    const openCount = Number(root.openCount ?? 0)
    const otherSprints = Array.isArray(root.otherSprints)
        ? root.otherSprints
        : []
    return {
        completedCount: Number.isFinite(completedCount) ? completedCount : 0,
        openCount: Number.isFinite(openCount) ? openCount : 0,
        otherSprints,
    }
}

/**
 * POST support/main-task/epic — create epic on project board.
 * @param {{
 *   projectId: string,
 *   title: string,
 *   description?: string,
 *   position?: number
 * }} payload
 */
export const createMainTaskEpic = (payload) =>
    axiosNew({
        url: 'support/main-task/epic',
        method: 'POST',
        data: payload,
    })

/**
 * PUT support/main-task/epic/:id/position — reorder epic on board.
 * @param {{ epicId: string, projectId: string, position: number }} args
 */
export const updateMainTaskEpicPosition = ({ epicId, projectId, position }) =>
    axiosNew({
        url: `support/main-task/epic/${encodeURIComponent(epicId)}/position`,
        method: 'PUT',
        data: { projectId, position },
    })

/**
 * PUT support/main-task/epic/:id — update epic title/description.
 * @param {{ epicId: string, projectId: string, title: string, description?: string }} args
 */
export const updateMainTaskEpic = ({ epicId, projectId, title, description }) =>
    axiosNew({
        url: `support/main-task/epic/${encodeURIComponent(epicId)}`,
        method: 'PUT',
        data: {
            projectId,
            title,
            description: description ?? '',
        },
    })

/** Normalize API epic documents into board epic rows (preserve API order). */
export const normalizeEpicRows = (epicsData = []) =>
    (epicsData || []).map((e) => ({
        id: String(e._id),
        title: e.title || 'Untitled Epic',
        epicKey: e.epicKey != null ? String(e.epicKey) : '',
        raw: e,
    }))

/** Normalize API sprint documents into board sprint rows (preserve API order). */
export const normalizeSprintRows = (sprintsData = []) =>
    (sprintsData || []).map((s) => ({
        id: String(s._id),
        title: s.title || 'Untitled Sprint',
        raw: s,
    }))

/**
 * Merge tasks from epic + sprint payloads; fill buckets[sprintId][epicId].
 * Tasks without an epic, or whose epic is not in the project epic list, are omitted.
 * Tasks without a matching sprint row use backlog / first sprint when possible.
 */
export const buildSprintBoardMatrix = (epicsData = [], sprintsData = []) => {
    const epics = normalizeEpicRows(epicsData)
    const sprints = normalizeSprintRows(sprintsData)

    const backlogSprint = sprintsData.find((s) =>
        /backlog/i.test(String(s?.title || ''))
    )
    const fallbackSprintId = backlogSprint?._id
        ? String(backlogSprint._id)
        : sprints[0]?.id

    const taskEntries = []
    const pushEntry = (task, sprintId, fromSnapshot = false) => {
        if (!task) return
        const ownId = task?._id || task?.taskId?._id
        if (!ownId) return
        taskEntries.push({
            task,
            taskId: String(ownId),
            sprintId: sprintId ? String(sprintId) : null,
            fromSnapshot: !!fromSnapshot,
        })
    }

    sprintsData.forEach((s) => {
        const sid = s?._id ? String(s._id) : null
        ;(s?.tasks || []).forEach((t) => {
            pushEntry(t, sid, !!t?._isSnapshot)
        })
    })
    epicsData.forEach((e) => {
        ;(e?.tasks || []).forEach((t) => pushEntry(t, null, false))
    })

    const epicIds = new Set(epics.map((e) => e.id))

    const buckets = {}
    const ensure = (sid, eid) => {
        if (!buckets[sid]) buckets[sid] = {}
        if (!buckets[sid][eid]) buckets[sid][eid] = []
    }

    sprints.forEach((s) => {
        epics.forEach((e) => ensure(s.id, e.id))
    })

    const normSprintId = (task, entrySprintId) => {
        if (entrySprintId) return entrySprintId
        const s = task?.sprintId
        if (!s) return fallbackSprintId || null
        if (typeof s === 'string') return s
        return String(s._id || '')
    }

    const toEpicId = (ex) => {
        if (!ex) return null
        if (typeof ex === 'string') return ex
        if (ex?._id) return String(ex._id)
        if (Array.isArray(ex) && ex[0]) return String(ex[0]._id || ex[0])
        return null
    }

    const normEpicId = (task) => {
        const primary = toEpicId(task?.epic)
        if (primary && epicIds.has(primary)) return primary
        const fallback = toEpicId(task?.taskId?.epic)
        if (fallback && epicIds.has(fallback)) return fallback
        return null
    }

    const seen = new Set()
    taskEntries.forEach(({ task, taskId, sprintId, fromSnapshot }) => {
        if (task?.parentTask?.length > 0) return
        const dedupeKey = fromSnapshot
            ? `${taskId}::snapshot::${String(sprintId || '')}`
            : `${taskId}::live`
        if (seen.has(dedupeKey)) return
        seen.add(dedupeKey)

        let sid = normSprintId(task, sprintId)
        if (!sid || !buckets[sid]) {
            if (fallbackSprintId && buckets[String(fallbackSprintId)]) {
                sid = String(fallbackSprintId)
            } else return
        }
        const eid = normEpicId(task)
        if (!eid || !buckets[sid][eid]) return
        buckets[sid][eid].push(task)
    })

    return {
        epics,
        sprints,
        buckets,
    }
}
